import { forwardRef, useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import { ko, enUS } from 'date-fns/esm/locale';
import { IPageInfo, IUser, pageInfoState } from '../../atoms';
import { DayPicker } from './PeriodPicker';
import { todayYYYYMMDD } from '../../utils/formatDate';
import { getForwardDateRange } from '../../utils/getForwardDateRange';
import { getWeekAlignedQueryRange } from '../../utils/getWeekAlignedQueryRange';
import SkeletonTable from '../SkeletonTable';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../services/_common';
import { copyContentsToClipboard } from '../../utils/copyContentsToClipboard';
import { useTranslation } from 'react-i18next';
import { appendApprovalStatus } from '../../utils/appendApprovalStatus';
import { isImageFile } from '../../utils/formatFilePath';
import { ImagePreview, PhotoImage } from '../ImagePreview';
import { useFetchHolidays } from '../../hooks/useFetchHolidays';
import i18n from '../../translation/i18n';
import { isMobile } from '../../utils/checkMobile';

// side-effect CSS: import 문은 ts(2882)가 나서 require 유지 (DatePicker.tsx와 동일 CSS)
require('react-datepicker/dist/react-datepicker.css');

const WeekCalendarStyle = styled.div`
  width: 100%;
  height: 100%;
  .widget-body {
    padding: 1rem 1rem 0 1rem;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    .week-calendar-header {
      flex-shrink: 0;
      .week-calendar-header-item {
        gap: 1rem;
        /* 남은 폭을 채워야 칩 축약 여부를 실제 가용 폭으로 판단할 수 있음 */
        flex: 1;
        min-width: 0;
        .day-date {
          flex: 1;
          min-width: 0;
        }
      }
      .week-calendar-header-item-picker > div {
        padding: 0.25rem 0 !important;
      }
      .week-calendar-header-item-buttons {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        /* DatePicker 래퍼가 inline-block/line-height로 아이콘 높이를 밀어 올리지 않도록 정렬 고정 */
        .datePickerWrapper {
          display: flex;
          align-items: center;
          height: 2rem;
          .react-datepicker-wrapper,
          .react-datepicker__input-container {
            display: flex !important;
            align-items: center;
            width: auto !important;
            height: 2rem;
            margin: 0;
            padding: 0;
            border: 0;
            line-height: 0;
          }
        }
      }
      .refresh-btn,
      .calendar-btn {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        border-radius: 2rem;
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 300, 'GRAD' 0, 'opsz' 20;
          color: var(--text-tertiary);
          opacity: 0.6;
        }
        &:hover {
          background-color: var(--tonal);
          .material-symbols-rounded {
            opacity: 1;
          }
        }
      }
      /* 일정명 ↔ 본사/현장 표시 전환 (일정 수 바로 옆) */
      .day-schedule-count-row {
        display: flex;
        flex-wrap: wrap; /* 아주 좁은 폭(폴드 커버 화면 등)에서는 칩이 일정 수 아래 줄로 내려감 */
        align-items: center;
        gap: 0.25rem 0.5rem;
        .day-schedule-count {
          white-space: nowrap;
        }
      }
      /* 켜면 일정 제목 자리에 본사/현장 표시 */
      .site-chip {
        /* 전역 button 스타일(height: 3rem, uppercase 등) 덮어쓰기, 높이는 일정 수 줄(1.5rem)과 동일 */
        flex-shrink: 0;
        height: 1.5rem;
        line-height: calc(1.5rem - 2px);
        white-space: nowrap;
        text-transform: none;
        cursor: pointer;
        padding: 0 0.625rem;
        border: 1px solid transparent;
        border-radius: 1rem;
        background-color: var(--tonal);
        font-size: 0.75rem;
        color: var(--text-tertiary);
        &.active {
          background-color: var(--tonal-blue);
          color: var(--selected-primary);
          font-weight: 600;
        }
      }
      /* 「현장 보기」 폭 측정용 (화면에 보이지 않음) */
      .site-chip-measure {
        position: absolute;
        visibility: hidden;
        pointer-events: none;
      }
      .calendar-btn {
        .material-symbols-rounded {
          color: var(--selected-primary);
          opacity: 0.8;
        }
        &:hover {
          .material-symbols-rounded {
            opacity: 1;
          }
        }
      }
      /* fixed popper에서도 다크모드 헤더/월 셀렉트가 보이도록 (GlobalStyle tab-loop 경로 보완) */
      .datePickerWrapper {
        .react-datepicker,
        .react-datepicker__header,
        .react-datepicker__month-container {
          background-color: var(--board);
          color: var(--text-primary);
        }
        .react-datepicker__day-name {
          color: var(--text-disabled);
        }
        .react-datepicker__day {
          color: var(--text-primary);
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          color: var(--btn-text);
          background-color: var(--selected-primary);
        }
        .custom-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.25rem 1rem;
          .selectGroup {
            display: flex;
            gap: 0.75em;
            .selectBox {
              position: relative;
              > span {
                position: absolute;
                right: 0.25rem;
                top: 50%;
                transform: translateY(-50%);
                pointer-events: none;
                font-size: 1.125rem;
                color: var(--text-disabled);
              }
              select {
                width: 5rem;
                color: var(--text-primary);
                background-color: transparent;
                option {
                  color: var(--text-primary);
                  background-color: var(--board);
                }
              }
            }
          }
          > .arrow {
            width: 1.5rem;
            height: 1.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 0.25rem;
            cursor: pointer;
            color: var(--text-primary);
            background-color: var(--tonal);
          }
        }
        /* 모바일(withPortal): 라이브러리 기본 풀스크린 오버레이. 딤 농도/z-index만 앱 기준으로 조정 */
        .react-datepicker__portal {
          z-index: 5100;
          background-color: rgba(0, 0, 0, 0.4);
          .react-datepicker {
            box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
          }
        }
      }
    }
    .day-number {
      font-size: 3rem;
      font-weight: 600;
      color: var(--text-primary);
      &.sunday {
        color: var(--red);
      }
      &.saturday {
        color: var(--blue);
      }
      &.holiday {
        color: var(--red);
      }
    }
    .day-schedule-count {
      font-size: 0.875rem;
      color: var(--text-secondary);
    }
    .week-calendar-body {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      flex: 1 1 0;
      min-height: 0;
      overflow-y: scroll;
      overflow-x: hidden;
      padding-right: 0.5rem;
      padding-bottom: 1rem;
      margin-top: 0.5rem;

      &::-webkit-scrollbar {
        width: 0.5rem;
      }
      &::-webkit-scrollbar-track {
        background: var(--tonal);
        border-radius: 0.25rem;
      }
      &::-webkit-scrollbar-thumb {
        background: var(--outline);
        border-radius: 0.25rem;
        &:hover {
          background: var(--text-tertiary);
        }
      }
    }

    .week-calendar-body-item {
      display: flex;
      cursor: pointer;
      border-radius: 0.5rem;
      margin-bottom: 0.25rem;
      transition: background-color 0.2s;

      &:hover {
        background-color: var(--tonal);
      }

      .week-calendar-body-item-name {
        font-size: 0.875rem;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .copy-icon {
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.5;
          transition: opacity 0.2s;
          flex-shrink: 0;

          &:hover {
            opacity: 1;
          }

          .material-symbols-rounded {
            font-size: 1rem;
            color: var(--text-secondary);
          }
        }
      }

      .back-color-bar {
        width: 0.25rem;
        min-width: 0.25rem;
        flex-shrink: 0;
        align-self: stretch;
        border-radius: 0.25rem;
        margin-right: 0.5rem;
      }

      .week-calendar-body-item-content {
        flex: 1;
        min-width: 0;
      }
      .detail-value-textarea {
        width: 100%;
        min-height: 2rem;
        border: none;
        background-color: transparent;
        resize: none;
        font-size: 0.875rem;
        color: var(--text-primary);
        overflow: hidden;
        line-height: 1.5;
        padding: 0;
        margin: 0;
      }

      .week-calendar-body-item-details {
        margin-top: 0.25rem;
        padding: 0.25rem 0;
        border-top: 1px solid var(--outline);
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        animation: slideDown 0.3s ease-out;

        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 500px;
          }
        }

        .detail-row {
          display: flex;
          gap: 0.5rem;
          font-size: 0.875rem;

          &.file-row {
            .image-list-wrap {
              display: flex;
              flex-wrap: wrap;
              gap: 0.5rem;
            }
          }

          .detail-label {
            color: var(--text-secondary);
            min-width: 4rem;
            display: flex;
            align-items: center;
            gap: 0.25rem;

            .copy-icon {
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0.5;
              transition: opacity 0.2s;
              flex-shrink: 0;

              &:hover {
                opacity: 1;
              }

              .material-symbols-rounded {
                font-size: 0.875rem;
                color: var(--text-secondary);
              }
            }
          }
          .detail-value {
            color: var(--text-primary);
            flex: 1;
          }
        }
      }
    }
    .week-calendar-body-item-attenders {
      font-size: 0.75rem;
      color: var(--text-secondary);
      margin-top: 0.25rem;
    }
  }
`;

// textarea 높이 자동 조정 컴포넌트
const AutoResizeTextarea = ({ value }: { value: string }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // 높이를 초기화하고 scrollHeight로 재설정
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value]);

  return <textarea ref={textareaRef} className='detail-value-textarea' value={value} readOnly />;
};

export const ThumbnailImage = ({ file }: { file: any }) => {
  return (
    <div className='image-item-wrap' onClick={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} role='presentation'>
      <PhotoImage file={file} size={100} />
    </div>
  );
};

const DateSelectButton = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ onClick, className, ...rest }, ref) => (
  <div {...rest} className={['calendar-btn', 'date-select-btn', className].filter(Boolean).join(' ')} onClick={onClick} ref={ref} role='button' tabIndex={0}>
    <span className='material-symbols-rounded'>calendar_today</span>
  </div>
));
DateSelectButton.displayName = 'DateSelectButton';

const MONTH_OPTIONS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

// 일정명 ↔ 본사/현장 스왑 토글 상태 저장 키 (브라우저별 유지)
const SHOW_SITE_STORAGE_KEY = 'week-calendar-show-site';

const getInitialShowSite = () => {
  try {
    return localStorage.getItem(SHOW_SITE_STORAGE_KEY) === 'Y';
  } catch {
    return false;
  }
};

const WeekCalendar = ({ userInfo }: { userInfo: IUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const today = todayYYYYMMDD();
  const [rangeStart, setRangeStart] = useState(today);
  const [selectedDayString, setSelectedDayString] = useState(today);
  const [expandedScheduleId, setExpandedScheduleId] = useState<number | string | null>(null);
  const [showSite, setShowSite] = useState(getInitialShowSite); // true: 제목 자리에 본사/현장, 상세에 일정명 표시
  const [isChipCompact, setIsChipCompact] = useState(false); // 「현장 보기」가 일정 수 옆에 들어가지 않으면 「현장」으로 축약
  const countRowRef = useRef<HTMLDivElement>(null);
  const countRef = useRef<HTMLDivElement>(null);
  const chipMeasureRef = useRef<HTMLSpanElement>(null);
  const setPageInfo = useSetRecoilState<IPageInfo>(pageInfoState);
  const hasCalendarMenuAuth = userInfo.userMenuList?.some((el: any) => el.mtCd === 'C001' && el.mdCd === '001' && el.mrCd === '001');
  const { holidays, isLoading: holidaysLoading, isError: holidaysError } = useFetchHolidays(dayjs(selectedDayString).format('YYYY'));

  const onClickDate = (dateParam: string) => {
    setSelectedDayString(dateParam.replaceAll('-', ''));
    setExpandedScheduleId(null); // 날짜 변경 시 펼쳐진 일정 닫기
  };

  /**
   * 수정자 : 홍선영
   * 수정일자 : 2026-08-10
   * 수정내용 : 날짜 선택 시 주간 조회 시작일과 선택일을 함께 변경
   */
  const onSelectCalendarDate = (date: Date | null) => {
    if (!date) return;
    const next = dayjs(date).format('YYYYMMDD');
    setRangeStart(next);
    setSelectedDayString(next);
    setExpandedScheduleId(null);
  };

  const onChangeShowSite = (next: boolean) => {
    setShowSite(next);
    try {
      localStorage.setItem(SHOW_SITE_STORAGE_KEY, next ? 'Y' : 'N');
    } catch {
      // 저장 불가 환경에서는 현재 화면에만 적용
    }
  };

  const onClickSchedule = (scheduleId: number | string) => {
    setExpandedScheduleId(expandedScheduleId === scheduleId ? null : scheduleId);
  };

  const onKeyDownSchedule = (e: React.KeyboardEvent, scheduleId: number | string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClickSchedule(scheduleId);
    }
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr || timeStr.length < 4) return '';
    return `${timeStr.substring(0, 2)}:${timeStr.substring(2, 4)}`;
  };

  const { data: companyUser, isSuccess: isSuccessCompanyUser } = useQuery(['/user/company_schedule', userInfo.hCd], () => getCompanyUserAPI(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c',
    cacheTime: 1000 * 60 * 60 * 24,
    staleTime: 1000 * 60 * 60 * 24, // 24시간 캐시
  });

  // 화면 표시 구간 : 선택한 시작일부터 7일 (DayPicker와 동일)
  const { start: viewStart, end: viewEnd } = useMemo(() => getForwardDateRange(rangeStart, 7), [rangeStart]);

  /**
   * 수정자 : 홍선영
   * 수정일자 : 2026-08-24
   * 수정내용 : 조회 구간을 표시 구간을 감싸는 주 경계(일요일~토요일)로 맞춤.
   *            표시 구간 7일(start+6)을 그대로 요청하면 서버 달력 보드가 그 주 토요일에서 끊겨,
   *            선택일이 일요일이 아닐 때 구간 꼬리 날짜의 일정이 누락된다.
   *            (BE schedule.service.ts getCalendarSize / createCalendarBoard 참고)
   *            표시는 viewStart~viewEnd 7일 그대로이고, 여분 데이터는 filteredSchedules에서 선택일 기준으로 걸러진다.
   */
  const { start: queryStart, end: queryEnd } = useMemo(() => getWeekAlignedQueryRange(viewStart, viewEnd), [viewStart, viewEnd]);

  // 일정 데이터 조회 (표시 구간을 감싸는 주 경계 구간)
  const {
    data: schedules,
    isLoading: isLoadingSchedules,
    refetch: refetchSchedule,
  } = useQuery(['schedules', queryStart, queryEnd], () => fetchData(queryStart, queryEnd), {
    keepPreviousData: true,
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c' && isSuccessCompanyUser && !!companyUser,
    refetchOnWindowFocus: true, // 백그라운드(앱 전환·화면 꺼짐·다른 탭)에서 돌아올 때마다 최신 일정으로 갱신
  });

  // 일정 데이터 조회 함수
  const fetchData = async (sDate: string, eDate: string) => {
    const defaultSearch = {
      cCd: userInfo.hCd,
      date1: sDate?.replaceAll('-', ''),
      date2: eDate?.replaceAll('-', ''),
      smGubun: '',
      completeYn: '',
      hCd: '',
      csCd: '',
      search: '',
    };
    const req = defaultSearch;
    const res = await apiGet({ path: '/schedule/calendar', req });

    if (res.status === 200) {
      const setData = res.data.data.map((el: any) => {
        return {
          ...el,
          scheduleFList: (el.scheduleFList || []).filter((file: any) => isImageFile(file.fUrl)),
          userName: el.joinUsers?.join(', ') || '',
        };
      });
      return setData;
    }
    return res;
  };

  // 사내 사용자 조회
  const getCompanyUserAPI = async () => {
    const req = { hCd: userInfo.hCd };
    const res = await apiGet({ path: '/user/company', req });
    try {
      const validUsers = res.data.data.filter((el: any) => el.approvalYn === 'Y' && el.useYn === 'Y'); // 승인 및 사용여부 Y 인것만 필터링
      return validUsers;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // selectedDayString을 YYYYMMDD 형식으로 정규화 (하이픈 제거)
  const selectedDateStr = useMemo(() => selectedDayString.replaceAll('-', ''), [selectedDayString]);

  // repeatDateList를 flatmap하여 일정 확장 (useCalendar.ts 로직 참고)
  const expandedSchedules = useMemo(() => {
    if (!Array.isArray(schedules)) return [];

    const formatSchedule = schedules
      .map((el: any) => {
        const { dGubun, repeatDateList, sDate, fDate, sTime, fTime, ...rest } = el;

        // repeatDateList가 빈 배열이면 제외
        if (Array.isArray(repeatDateList) && repeatDateList.length === 0) {
          return null;
        }

        // repeatDateList가 있으면 각 날짜마다 일정 생성
        if (repeatDateList && Array.isArray(repeatDateList) && repeatDateList.length > 0) {
          return repeatDateList.map((el2: any, rIdx: number) => {
            // dGubun이 있으면 repeatDateList의 날짜 사용, 없으면 원본 날짜 사용
            const startDateStr = dGubun ? el2.startDate?.substring(0, 10)?.replaceAll('-', '') : sDate;
            const endDateStr = dGubun ? el2.endDate?.substring(0, 10)?.replaceAll('-', '') : fDate;

            return {
              ...rest,
              ...el,
              sDate: startDateStr,
              fDate: endDateStr,
              orgRepeatSdate: el2.startDate?.substring(0, 10)?.replaceAll('-', ''),
              orgSdate: sDate,
              orgFdate: fDate,
              isRepeatSchedule: !!dGubun,
              scheduleFList: (el.scheduleFList || []).map((file: any) => ({
                ...file,
                // fileId: file.fIndex,
                // file: file.fUrl,
                fileName: file.fUrl.slice(file.fUrl.lastIndexOf('/') + 1),
                fileFormat: file.fUrl ? file.fUrl.slice(file.fUrl.lastIndexOf('.') + 1) : '',
              })),
            };
          });
        }

        // repeatDateList가 없으면 원본 일정 그대로 반환
        return [el];
      })
      .filter((item) => item !== null); // null 제거

    return formatSchedule.flat();
  }, [schedules]);

  // selectedDayString에 맞는 일정만 필터링 (멀티데이 일정 고려)
  const filteredSchedules = useMemo(() => {
    if (!Array.isArray(expandedSchedules)) return [];

    const schedulesOfDay = expandedSchedules.filter((schedule: any) => {
      // 반복 일정인 경우: orgRepeatSdate와 선택된 날짜가 정확히 일치해야 함
      if (schedule.isRepeatSchedule && schedule.orgRepeatSdate) {
        const repeatDateStr = String(schedule.orgRepeatSdate).replaceAll('-', '');
        return repeatDateStr === selectedDateStr;
      }

      // 일반 일정인 경우: sDate ~ fDate 범위 내에 포함되는지 확인
      const scheduleStartDate = String(schedule.sDate || '').replaceAll('-', '');
      const scheduleEndDate = String(schedule.fDate || '').replaceAll('-', '');

      // 시작일과 종료일이 모두 있어야 함
      if (!scheduleStartDate || !scheduleEndDate) return false;

      // 선택된 날짜가 일정 기간(sDate ~ fDate) 내에 포함되는지 확인
      return selectedDateStr >= scheduleStartDate && selectedDateStr <= scheduleEndDate;
    });

    // 개인 근태(휴가, smGubun 4) 일정은 항상 가장 마지막에 표시 (나머지는 기존 순서 유지)
    return [...schedulesOfDay.filter((schedule: any) => schedule.smGubun !== '4'), ...schedulesOfDay.filter((schedule: any) => schedule.smGubun === '4')];
  }, [expandedSchedules, selectedDateStr]);

  // 일정 수 줄의 가용 폭에 「현장 보기」 칩이 들어가지 않으면(줄바꿈 발생 시) 「현장」으로 축약
  useLayoutEffect(() => {
    const row = countRowRef.current;
    if (!row) return undefined;

    const updateChipCompact = () => {
      if (!countRef.current || !chipMeasureRef.current) return;
      // 소수점 단위 폭으로 비교해야 경계 폭에서 반올림 오차로 줄바꿈되는 경우가 없음
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      const needed = countRef.current.getBoundingClientRect().width + gap + chipMeasureRef.current.getBoundingClientRect().width;
      setIsChipCompact(row.getBoundingClientRect().width < needed);
    };

    updateChipCompact();
    const observer = new ResizeObserver(updateChipCompact);
    observer.observe(row);
    return () => observer.disconnect();
  }, [filteredSchedules.length, showSite]);

  const onClickRefresh = () => {
    const todayStr = todayYYYYMMDD();
    setRangeStart(todayStr);
    setSelectedDayString(todayStr);
    setExpandedScheduleId(null);
    // 이미 오늘 구간이면 queryKey가 안 바뀌므로 수동 refetch
    if (rangeStart === todayStr) {
      refetchSchedule();
    }
  };

  // 백그라운드에서 돌아왔을 때 그사이 날짜가 바뀌었고 숨겨질 때 '오늘'을 보고 있었으면 새 오늘로 이동
  // (데이터 갱신 자체는 일정 조회의 refetchOnWindowFocus가 담당, 다른 날짜를 보고 있었으면 그 날짜 유지)
  const hiddenDayRef = useRef('');
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenDayRef.current = todayYYYYMMDD();
        return;
      }

      const todayStr = todayYYYYMMDD();
      if (hiddenDayRef.current && hiddenDayRef.current !== todayStr && selectedDayString === hiddenDayRef.current) {
        setRangeStart(todayStr);
        setSelectedDayString(todayStr);
        setExpandedScheduleId(null);
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [selectedDayString]);

  const onClickCalendarMonth = () => {
    if (hasCalendarMenuAuth) {
      navigate('/c_work1?tabCd=001');
      setPageInfo((prev) => ({
        ...prev,
        defaultMrCd: '001',
        mtCd: 'C001',
        mdCd: '001',
        mrCd: '001',
      }));
    }
  };

  /*
  const onClickImage = (file: any, e: React.MouseEvent) => {
    e.stopPropagation(); // 일정 클릭 이벤트 전파 방지

    setOpenModal({
      status: true,
      type: 'thumbnail',
      data: { aImgPath: file.fUrl, fileName: file.fileName, fileFormat: file.fileFormat },
      thumbnailOnly: true,
    });
  };
  */

  const onClickCopyTitle = (copyText: string, e: React.MouseEvent) => {
    copyContentsToClipboard(e, copyText);
  };

  return (
    <WeekCalendarStyle>
      <div className='widget-body'>
        <div className='week-calendar-header'>
          <div className='flex-between' style={{ alignItems: 'flex-start' }}>
            <div className='week-calendar-header-item flex-basic'>
              <div
                className={`day-number ${
                  !holidaysLoading && !holidaysError && holidays.length > 0 && holidays.some((holiday) => dayjs(holiday.sDate).format('MMDD') === dayjs(selectedDayString).format('MMDD'))
                    ? 'holiday'
                    : ''
                } ${dayjs(selectedDayString).format('dddd').charAt(0) === '일' ? 'sunday' : dayjs(selectedDayString).format('dddd').charAt(0) === '토' ? 'saturday' : ''}`}
              >
                {dayjs(selectedDayString).format('DD')}
              </div>
              <div className='day-date'>
                <div>
                  {dayjs(selectedDayString).format('MMMM')}
                  {!holidaysLoading &&
                    !holidaysError &&
                    holidays.length > 0 &&
                    (() => {
                      const selectedDateMMDD = dayjs(selectedDayString).format('MMDD');
                      const matchedHoliday = holidays.find((holiday) => dayjs(holiday.sDate).format('MMDD') === selectedDateMMDD);
                      return matchedHoliday?.smName ? ` · ${matchedHoliday.smName}` : '';
                    })()}
                </div>
                <div className='day-schedule-count-row' ref={countRowRef}>
                  <div className='day-schedule-count' ref={countRef}>
                    {filteredSchedules.length > 0 ? `${filteredSchedules.length} ${t('개 일정')}` : `${t('일정 없음')}`}
                  </div>
                  <button
                    type='button'
                    className={`site-chip ${showSite ? 'active' : ''}`}
                    onClick={() => onChangeShowSite(!showSite)}
                    aria-pressed={showSite}
                    title={showSite ? t('일정명 보기') : t('본사/현장 보기')}
                  >
                    {isChipCompact ? t('현장') : t('현장 보기')}
                  </button>
                  <span className={`site-chip site-chip-measure ${showSite ? 'active' : ''}`} ref={chipMeasureRef} aria-hidden='true'>
                    {t('현장 보기')}
                  </span>
                </div>
              </div>
            </div>
            <div className='week-calendar-header-item-buttons flex-between'>
              <div className='datePickerWrapper'>
                <DatePicker
                  selected={dayjs(selectedDayString).toDate()}
                  onChange={onSelectCalendarDate}
                  customInput={<DateSelectButton aria-label={t('날짜 선택')} />}
                  locale={i18n.language === 'ko-KR' ? ko : enUS}
                  dateFormat='yyyy-MM-dd'
                  /* 모바일은 라이브러리 기본 풀스크린 포털 사용: 위젯 overflow를 벗어나고, 바깥 터치로 닫힐 때
                     react-datepicker가 preventDefault를 걸어줘서 뒤따르는 ghost click이 아래 요소로 통과하지 않음 */
                  withPortal={isMobile()}
                  popperPlacement='bottom'
                  popperClassName='week-calendar-date-popper'
                  popperProps={{ strategy: 'fixed' }}
                  popperModifiers={[
                    {
                      name: 'preventOverflow',
                      options: { rootBoundary: 'viewport', padding: 8 },
                    },
                  ]}
                  shouldCloseOnSelect
                  renderCustomHeader={({ date, changeMonth, decreaseMonth, increaseMonth }) => (
                    <div className='custom-header'>
                      <div className='arrow' onClick={decreaseMonth} role='button' tabIndex={0}>
                        <span className='material-symbols-outlined'>arrow_left</span>
                      </div>
                      <div className='selectGroup'>
                        <div className='selectBox'>
                          <select value={MONTH_OPTIONS[date.getMonth()]} onChange={({ target: { value } }) => changeMonth(MONTH_OPTIONS.indexOf(value))}>
                            {MONTH_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                                {t('월')}
                              </option>
                            ))}
                          </select>
                          <span className='material-symbols-outlined'>keyboard_arrow_down</span>
                        </div>
                      </div>
                      <div className='arrow' role='button' tabIndex={0} onClick={increaseMonth}>
                        <span className='material-symbols-outlined'>arrow_right</span>
                      </div>
                    </div>
                  )}
                />
              </div>
              {hasCalendarMenuAuth && (
                <div className='calendar-btn' onClick={onClickCalendarMonth} role='presentation'>
                  <span className='material-symbols-rounded'>exit_to_app</span>
                </div>
              )}
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
          </div>
          <div className='week-calendar-header-item-picker'>
            <DayPicker dayCount={7} direction='future' baseDate={rangeStart} selectedValue={selectedDayString} handleSelect={onClickDate} />
          </div>
        </div>
        <div className='week-calendar-body'>
          {isLoadingSchedules ? (
            <div style={{ width: '100%', height: '100%' }}>
              <SkeletonTable rows={9} />
            </div>
          ) : (
            filteredSchedules?.map((schedule: any) => {
              // 결재중·보류 근태는 smIdx가 없으므로 근태계 번호(atCd)로 항목을 구분
              const scheduleKey = schedule.smIdx ?? `at-${schedule.atCd}`;
              const isExpanded = expandedScheduleId === scheduleKey;
              // 본사·현장이 모두 있는 일정만 일정명과 본사/현장 위치를 맞바꿈 (상세의 현장 행 표시 조건과 동일)
              const isSiteSwapped = showSite && !!schedule.hName && !!schedule.sName;
              return (
                <div key={scheduleKey} className='week-calendar-body-item' role='button' tabIndex={0} onClick={() => onClickSchedule(scheduleKey)} onKeyDown={(e) => onKeyDownSchedule(e, scheduleKey)}>
                  <div
                    className='back-color-bar'
                    style={{
                      backgroundColor:
                        schedule.smGubun === '3'
                          ? schedule.scheduleSet?.backColor
                            ? schedule.scheduleSet?.backColor
                            : 'var(--filled-green-deep)'
                          : schedule.smGubun === '4'
                          ? 'var(--tonal)' // 개인 근태는 캘린더 근태 일정 바탕색과 동일한 회색
                          : 'var(--filled-violet)',
                    }}
                  />
                  <div className='week-calendar-body-item-content'>
                    <div className='week-calendar-body-item-name'>
                      <span>{isSiteSwapped ? `${schedule.hName}/${schedule.sName}` : appendApprovalStatus(schedule.smName, schedule.approvalStatus)}</span>
                      <div className='copy-icon' onClick={(e) => onClickCopyTitle(`${schedule.hName || ''}/${schedule.sName || ''}//${schedule.smName || ''}`, e)} role='button' tabIndex={0}>
                        <span className='material-symbols-rounded'>content_copy</span>
                      </div>
                    </div>
                    <div className='week-calendar-body-item-attenders'>{schedule.userName}</div>
                    {isExpanded && (
                      <div className='week-calendar-body-item-details'>
                        {schedule.alldayYn !== 'Y' && schedule.sTime && schedule.fTime && (
                          <div className='detail-row'>
                            <span className='detail-label'>
                              시간
                              <div className='copy-icon' onClick={(e) => onClickCopyTitle(`${formatTime(schedule.sTime)} ~ ${formatTime(schedule.fTime)}`, e)} role='button' tabIndex={0}>
                                <span className='material-symbols-rounded'>content_copy</span>
                              </div>
                            </span>
                            <span className='detail-value'>
                              {formatTime(schedule.sTime)} ~ {formatTime(schedule.fTime)}
                            </span>
                          </div>
                        )}
                        {isSiteSwapped && (
                          <div className='detail-row'>
                            <span className='detail-label'>
                              {t('일정명')}
                              <div className='copy-icon' onClick={(e) => onClickCopyTitle(schedule.smName || '', e)} role='button' tabIndex={0}>
                                <span className='material-symbols-rounded'>content_copy</span>
                              </div>
                            </span>
                            <span className='detail-value'>{schedule.smName}</span>
                          </div>
                        )}
                        {!isSiteSwapped && schedule.hName && schedule.sName && (
                          <div className='detail-row'>
                            <span className='detail-label'>
                              {t('현장')}
                              <div className='copy-icon' onClick={(e) => onClickCopyTitle(`${schedule.hName || ''}/${schedule.sName || ''}//${schedule.smName || ''}`, e)} role='button' tabIndex={0}>
                                <span className='material-symbols-rounded'>content_copy</span>
                              </div>
                            </span>
                            <span className='detail-value'>
                              {schedule.hName} / {schedule.sName}
                            </span>
                          </div>
                        )}
                        {schedule.address && (
                          <div className='detail-row'>
                            <span className='detail-label'>
                              {t('주소')}
                              <div className='copy-icon' onClick={(e) => onClickCopyTitle(schedule.address, e)} role='button' tabIndex={0}>
                                <span className='material-symbols-rounded'>content_copy</span>
                              </div>
                            </span>
                            <span className='detail-value'>{schedule.address}</span>
                          </div>
                        )}

                        {schedule.siteManagerName && (
                          <div className='detail-row'>
                            <span className='detail-label'>
                              {t('담당자')}
                              <div
                                className='copy-icon'
                                onClick={(e) =>
                                  onClickCopyTitle(`${schedule.siteManagerName ?? ''} ${schedule.siteManagerPartname !== '' ? schedule.siteManagerPartname : ''} ${schedule.siteManagerPhone ?? ''}`, e)
                                }
                                role='button'
                                tabIndex={0}
                              >
                                <span className='material-symbols-rounded'>content_copy</span>
                              </div>
                            </span>
                            <span className='detail-value'>
                              {schedule.siteManagerName ?? ''} {schedule.siteManagerPartname !== '' ? schedule.siteManagerPartname : ''} {schedule.siteManagerPhone ?? ''}
                            </span>
                          </div>
                        )}
                        {schedule.scheduleSet?.csName && (
                          <div className='detail-row'>
                            <span className='detail-label'>
                              {t('일정구분')}
                              <div className='copy-icon' onClick={(e) => onClickCopyTitle(schedule.scheduleSet?.csName || '', e)} role='button' tabIndex={0}>
                                <span className='material-symbols-rounded'>content_copy</span>
                              </div>
                            </span>
                            <span className='detail-value'>{schedule.scheduleSet?.csName}</span>
                          </div>
                        )}
                        {/* 휴가(근태) 일정은 사유 등 개인정보 보호를 위해 내용 미표시 */}
                        {schedule.contents && schedule.smGubun !== '4' && (
                          <div className='detail-row'>
                            <span className='detail-label'>
                              {t('내용')}
                              <div className='copy-icon' onClick={(e) => onClickCopyTitle(schedule.contents, e)} role='button' tabIndex={0}>
                                <span className='material-symbols-rounded'>content_copy</span>
                              </div>
                            </span>
                            <AutoResizeTextarea value={schedule.contents} />
                          </div>
                        )}
                        {/** 첨부파일 이미지 썸네일 표시 */}
                        {schedule.scheduleFList && schedule.scheduleFList.length > 0 && (
                          <div className='detail-row file-row'>
                            <span className='detail-label'>{t('첨부 파일')}</span>
                            <span className='detail-value' style={{ display: 'flex', gap: '0.5rem' }}>
                              <div className='image-list-wrap'>
                                <ImagePreview imageList={schedule.scheduleFList.filter((file: any) => isImageFile(file.fUrl))}>
                                  {schedule.scheduleFList
                                    .filter((file: any) => isImageFile(file.fUrl))
                                    .map((file: any) => (
                                      <ThumbnailImage key={file.fIndex} file={file} />
                                    ))}
                                </ImagePreview>
                              </div>
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {/* <Portal openModal={openModal?.status}>{openModal.status && openModal.type === 'thumbnail' && <Thumbnail openModal={openModal} setOpenModal={setOpenModal} download />}</Portal> */}
    </WeekCalendarStyle>
  );
};

export default WeekCalendar;
